import { imageLinks } from 'assets/images';
import React, { useEffect, useMemo, useState } from 'react';
import { IMessage, IMessageAction } from 'services/interface';
import { notifyEmbedParent } from 'utils/embedMessaging';
import { useToast } from 'contexts/ToastContext';
import MessageForm from './MessageForm/MessageForm';
import { convertMarkdownToHTML } from 'utils/helper';
import VoiceNote from './VoiceNote/VoiceNote';
import Base64AudioPlayer from './VoiceNote/VoiceNoteAudio';
import StructuredMessage from './StructuredMessage/StructuredMessage';
import { DocumentMessage, GridMessage } from './BillMessage';
import "./Message.scss";

const CATEGORY_PARENT_ACTION_PREFIX = 'local_open_category::';
const CATEGORY_BACK_ACTION = 'local_actions_go_back';
const OPEN_DATA_CALCULATOR_ACTION = 'local_open_data_calculator';

/** Open CTA URLs as absolute external links (never resolve against the app origin). */
const toExternalUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) {
        return trimmed;
    }
    if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }
    return `https://${trimmed.replace(/^\/+/, '')}`;
};


interface MessageProps {
    message: IMessage;
    handleSendMessage: (value: string, action?: string, type?: string) => void;
    handleRetryVoiceUpload: (messageId: string) => void;
    isFirstInGroup: boolean;
    isLastInGroup: boolean;
    isCompleted?: boolean;
    pausePlaybackSignal?: number;
    onGridSubmit?: (label: string, value: string) => void;
}

const Message: React.FC<MessageProps> = ({
    message,
    handleSendMessage,
    handleRetryVoiceUpload,
    isFirstInGroup,
    isLastInGroup,
    isCompleted = false,
    pausePlaybackSignal = 0,
    onGridSubmit
}) => {
    const { showToast } = useToast();
    const getMessageGroupClass = () => {
        if (isFirstInGroup && !isLastInGroup) return 'message-group--first';
        if (!isFirstInGroup && !isLastInGroup) return 'message-group--middle';
        if (!isFirstInGroup && isLastInGroup) return 'message-group--last';
        return '';
    };

    // Check if audioSource is base64 (string that's either data URI or long base64 string)
    const isBase64Audio = (audioSource: string | Blob | undefined): audioSource is string => {
        if (!audioSource || audioSource instanceof Blob) return false;
        // Check if it's a data URI
        if (audioSource.startsWith('data:audio/')) return true;
        // Check if it's a long base64 string (likely audio)
        if (audioSource.length > 100 && /^[A-Za-z0-9+/=]+$/.test(audioSource.replace(/^data:.*;base64,/, ''))) {
            return true;
        }
        return false;
    };

    const structuredSections = message.payload?.structuredSections || [];
    const hasStructuredSections = structuredSections.length > 0;

    // Group the action buttons by their (optional) `category`. Actions
    // without a category render at the top level alongside the category
    // parent buttons. Tapping a parent button drills down to that
    // category's actions; a "Go back" button returns to the top level.
    const { uncategorizedActions, categoryOrder, categorizedActions } = useMemo(() => {
        const uncategorized: NonNullable<IMessage['actions']> = [];
        const order: string[] = [];
        const grouped: Record<string, NonNullable<IMessage['actions']>> = {};

        message.actions?.forEach(action => {
            if (action.category) {
                if (!grouped[action.category]) {
                    grouped[action.category] = [];
                    order.push(action.category);
                }
                grouped[action.category].push(action);
            } else {
                uncategorized.push(action);
            }
        });

        return {
            uncategorizedActions: uncategorized,
            categoryOrder: order,
            categorizedActions: grouped
        };
    }, [message.actions]);

    const hasCategories = categoryOrder.length > 0;
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [imageFailed, setImageFailed] = useState(false);
    const [documentUrlAvailable, setDocumentUrlAvailable] = useState(
        message.type !== 'document' || Boolean(
            message.payload?.document?.url &&
            message.payload.document.expiresAt &&
            new Date(message.payload.document.expiresAt).getTime() > Date.now()
        )
    );
    const activeCategoryActions = selectedCategory ? categorizedActions[selectedCategory] : undefined;
    const imageTitle = message.imageTitle?.trim() || undefined;
    const imageLink = message.imageLink?.trim() || undefined;
    const imageAction = message.imageAction?.trim() || undefined;
    const hasImageAction = Boolean(imageAction);
    const hasImageCta = Boolean(imageTitle && imageLink) || hasImageAction;
    const externalImageLink = imageLink ? toExternalUrl(imageLink) : undefined;

    useEffect(() => {
        setImageFailed(false);
    }, [message.image]);

    const openExternalHref = (href: string) => {
        try {
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'OPEN_URL',
                    url: href
                }));
                return;
            }

            if (window.parent !== window) {
                notifyEmbedParent('external_url', { url: href });
            }

            window.open(href, '_blank', 'noopener,noreferrer');
        } catch {
            window.location.href = href;
        }
    };

    const handleImageLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (!externalImageLink) {
            return;
        }
        openExternalHref(externalImageLink);
    };

    const handleImageActionClick = () => {
        if (!imageAction) {
            return;
        }
        handleSendMessage(imageTitle || imageAction, imageAction, 'submit');
    };

    const bannerMedia = (() => {
        if (message.video) {
            return (
                <video
                    src={message.video}
                    className="agent__video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    disablePictureInPicture
                    controls={false}
                    aria-label={imageTitle || 'Welcome banner'}
                    title={imageTitle}
                />
            );
        }

        if (imageFailed || !message.image) {
            return (
                <div
                    className="agent__image-placeholder"
                    role="img"
                    aria-label="Banner image unavailable"
                >
                    <span className="agent__image-placeholder__title">
                    </span>
                </div>
            );
        }

        return (
            <img
                src={message.image}
                alt={imageTitle || 'Agent message'}
                title={imageTitle}
                className="agent__image"
                onError={() => setImageFailed(true)}
            />
        );
    })();

    const handleClientAction = async (action: IMessageAction) => {
        if (!action.url) {
            return;
        }

        if (action.type === 'open_url') {
            openExternalHref(action.url);
            return;
        }

        if (action.type === 'copy_link') {
            try {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(action.url);
                } else {
                    const input = document.createElement('textarea');
                    input.value = action.url;
                    input.style.position = 'fixed';
                    input.style.opacity = '0';
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    input.remove();
                }
                showToast('Link copied', { type: 'success' });
            } catch {
                showToast('Unable to copy link', { type: 'error' });
            }
        }
    };

    const handleActionClick = (action: string, label: string, type: string, actionData?: IMessageAction) => {
        if (type === 'open_url' || type === 'copy_link') {
            void handleClientAction(actionData || { action, label, type });
            return;
        }

        if (action.startsWith(CATEGORY_PARENT_ACTION_PREFIX)) {
            const category = action.slice(CATEGORY_PARENT_ACTION_PREFIX.length);
            if (categorizedActions[category]) {
                setSelectedCategory(category);
            }
            return;
        }

        if (action === CATEGORY_BACK_ACTION) {
            setSelectedCategory(null);
            return;
        }

        handleSendMessage(label, action, type === 'submit' ? 'submit' : undefined);
    };

    type RenderableAction = {
        action: string;
        label: string;
        type: string;
        variant?: 'category' | 'back';
        data?: IMessageAction;
    };

    let renderableActions: RenderableAction[] = [];
    if (hasCategories) {
        if (activeCategoryActions && selectedCategory) {
            renderableActions = [
                ...activeCategoryActions.map(a => ({
                    action: a.action,
                    label: a.label,
                    type: a.type
                })),
                {
                    action: CATEGORY_BACK_ACTION,
                    label: '← Go back',
                    type: 'button',
                    variant: 'back'
                }
            ];
        } else {
            const calculatorAction = uncategorizedActions.find(action => action.action === OPEN_DATA_CALCULATOR_ACTION);
            const otherUncategorizedActions = uncategorizedActions.filter(action => action.action !== OPEN_DATA_CALCULATOR_ACTION);

            renderableActions = [
                ...categoryOrder.map(category => ({
                    action: `${CATEGORY_PARENT_ACTION_PREFIX}${category}`,
                    label: category,
                    type: 'button',
                    variant: 'category' as const
                })),
                ...(calculatorAction ? [{
                    action: calculatorAction.action,
                    label: calculatorAction.label,
                    type: calculatorAction.type
                }] : []),
                ...otherUncategorizedActions.map(a => ({
                    action: a.action,
                    label: a.label,
                    type: a.type
                }))
            ];
        }
    } else {
        renderableActions = (message.actions ?? [])
            .filter(action => !(message.type === 'document' && !documentUrlAvailable && (action.type === 'open_url' || action.type === 'copy_link')))
            .map(a => ({
                action: a.action,
                label: a.label,
                type: a.type,
                data: a
            }));
    }

    const billGrid = message.type === 'grid' ? message.payload?.grid : undefined;
    const billDocument = message.type === 'document' ? message.payload?.document : undefined;
    const handleGridSubmit = (item: { label: string; value: string }) => {
        if (onGridSubmit) {
            onGridSubmit(item.label, item.value);
            return;
        }
        handleSendMessage(item.label, item.value, 'submit');
    };

    return (
        <div
            key={message.id}
            className={`message__bubble ${message.sender} ${getMessageGroupClass()}`}
        >
            <>
                <div className="row ">
                    {message.sender === 'agent' && (
                        isLastInGroup ? (
                            <div className="col-2 flex-end">
                                <div className="sender__image">
                                    <img src={imageLinks.logo} alt="AI" />
                                </div>
                            </div>
                        ) : (
                            <div className="col-2" />
                        )
                    )}

                    <div className={message.sender === 'agent' ? 'col-10' : 'col-12'}>
                        <div className={`message__bubble--group ${message.type === 'audio' ? 'message__bubble--group--audio' : ''} ${hasStructuredSections ? 'message__bubble--group--structured-content' : ''}`}>
                            {message.sender === 'agent' && (message.video || message.image) && (
                                <div className="message__bubble--group--image">
                                    {hasImageAction && !message.video ? (
                                        <button
                                            type="button"
                                            className="agent__image-link agent__image-link--action"
                                            title={imageTitle}
                                            aria-label={imageTitle || 'Banner action'}
                                            onClick={handleImageActionClick}
                                        >
                                            {bannerMedia}
                                        </button>
                                    ) : hasImageCta && externalImageLink && !message.video ? (
                                        <a
                                            href={externalImageLink}
                                            className="agent__image-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={imageTitle}
                                            aria-label={imageTitle}
                                            onClick={handleImageLinkClick}
                                        >
                                            {bannerMedia}
                                        </a>
                                    ) : (
                                        bannerMedia
                                    )}
                                </div>
                            )}

                            {(Boolean(message.text) || hasStructuredSections || billGrid || billDocument || (message.type === 'audio' && message.payload?.audioSource)) && (
                                <div className={`message__bubble--group--text ${hasStructuredSections ? 'message__bubble--group--text-structured' : ''}`}>
                                    {message.type === 'audio' && message.payload?.audioSource && (
                                        <div className={message.sender === 'agent' ? 'agent__audio' : 'user__audio'} key={message.id}>
                                            {isBase64Audio(message.payload.audioSource) ? (
                                                <Base64AudioPlayer
                                                    audioSource={message.payload.audioSource}
                                                    uploadState={message.payload.uploadState}
                                                    errorMessage={message.payload.errorMessage}
                                                    onRetry={() => handleRetryVoiceUpload(message.id)}
                                                    pausePlaybackSignal={pausePlaybackSignal}
                                                />
                                            ) : (
                                                <VoiceNote
                                                    audioSource={message.payload.audioSource}
                                                    uploadState={message.payload.uploadState}
                                                    errorMessage={message.payload.errorMessage}
                                                    duration={message.payload.duration}
                                                    onRetry={() => handleRetryVoiceUpload(message.id)}
                                                    pausePlaybackSignal={pausePlaybackSignal}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {message.text && !hasStructuredSections && (
                                        <div>
                                            <div
                                                dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(message.text) }}
                                                onClick={(e) => {
                                                    const target = e.target as HTMLElement;
                                                    if (target.tagName === 'A') {
                                                        e.preventDefault();
                                                        const href = target.getAttribute('href');
                                                        if (href) {
                                                            try {
                                                                // Check if we're in a mobile environment
                                                                if (window.ReactNativeWebView) {
                                                                    // Send message to mobile app
                                                                    window.ReactNativeWebView.postMessage(JSON.stringify({
                                                                        type: 'OPEN_URL',
                                                                        url: href
                                                                    }));
                                                                } else {
                                                                    // Regular web browser behavior
                                                                    window.open(href, '_blank', 'noopener,noreferrer');
                                                                }
                                                            } catch (error) {
                                                                // Fallback to default behavior if something goes wrong
                                                                window.location.href = href;
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}

                                    {hasStructuredSections && (
                                        <div className="message__bubble--group--structured">
                                            <StructuredMessage sections={structuredSections} />
                                        </div>
                                    )}

                                    {billGrid && (
                                        <GridMessage
                                            grid={billGrid}
                                            onSubmit={handleGridSubmit}
                                        />
                                    )}

                                    {billDocument && (
                                        <DocumentMessage
                                            document={billDocument}
                                            onUrlAvailabilityChange={setDocumentUrlAvailable}
                                        />
                                    )}
                                </div>
                            )}


                            {message.type === 'form' && (message.formField) && ( // (message.type === 'form' && (message.formField) || message.type === 'group' && (message.fields))
                                <>
                                    {message.formField.label && message.formField.label !== '' && <div className="message__bubble--group--text message__bubble--group--text-label">
                                        <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(message.formField.label) }} />
                                    </div>}
                                    {message.formField.name && message.formField.name !== '' && <div className="message__bubble--group--form">
                                        <MessageForm formField={message.formField} handleSendMessage={handleSendMessage} />
                                    </div>
                                    }
                                </>

                            )}

                            {message.type === 'group' && (message.fields) && ( // (message.type === 'form' && (message.formField) || message.type === 'group' && (message.fields))
                                <>
                                    {message.fields.map((field, index) => (
                                        <div key={index} className="message__bubble--group--form">
                                            <MessageForm formField={{ ...field, actions: message.actions }} handleSendMessage={handleSendMessage} position={isLastInGroup}/>
                                        </div>
                                    ))}
                                </>
                            )}
                            {message.type === 'plain' && (message.fields) && ( 
                                <>
                                    {message.fields.map((field, index) => (
                                        <div key={index} className="message__bubble--group--form">
                                            <MessageForm formField={{ ...field, actions: message.actions }} handleSendMessage={handleSendMessage} position={isLastInGroup}/>
                                        </div>
                                    ))}
                                </>
                            )}

                        </div>
                    </div>
                </div>

                {message.sender === 'agent' && message.options && message.options.length > 0 && isLastInGroup && (
                    <div className="message__bubble--options">
                        {message.options.map((option, index) => {
                            const matchingAction = message?.payload?.actions?.find(action => action.label === option)
                            // Fall back to the option label so quick-reply clicks are tagged as button inputType.
                            const optionAction = matchingAction?.action || option;
                            return (
                                <div
                                    key={index}
                                    className='bubble__option'
                                    onClick={() => handleSendMessage(option, optionAction)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleSendMessage(option, optionAction);
                                        }
                                    }}
                                    role='button'
                                    tabIndex={index}
                                    aria-label={option}
                                >
                                    {option}
                                </div>
                            )
                        })
                        }
                    </div>
                )}
                {message.sender === 'agent'  && (!message.fields || message.fields?.length === 0) && message.type !== 'form' && renderableActions.length > 0 && isLastInGroup && (
                    <div className={`message__bubble--options${hasCategories && selectedCategory ? ' message__bubble--options--drilled' : ''}`}>
                        {
                            renderableActions.map(({ action, label, type, variant, data }, index) => {
                                const variantClass = variant === 'category'
                                    ? ' bubble__option--category'
                                    : variant === 'back'
                                        ? ' bubble__option--back'
                                        : '';
                                return (
                                    <div
                                        key={`${action}-${index}`}
                                        className={`bubble__option${variantClass}`}
                                        data-action={action}
                                        onClick={() => handleActionClick(action, label, type, data)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                handleActionClick(action, label, type, data);
                                            }
                                        }}
                                        role='button'
                                        tabIndex={index}
                                        aria-label={label}
                                    >
                                        {label}
                                    </div>
                                );
                            })
                        }
                    </div>
                )
                }
                
            </>
        </div>
    );
};

export default Message;
